import { LitElement, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { landingStyles } from './landing-page.styles.js';
import { pricingPageStyles } from './pricing-page.styles.js';

type PricingTier = {
  id: string;
  name: string;
  price: string;
  cadence: string;
  description: string;
  features: string[];
  highlight?: boolean;
};

const PRICING_TIERS: PricingTier[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: '£39',
    cadence: '/ month',
    description: 'Ideal for small teams getting started with digital maintenance.',
    features: [
      'Up to 5 users',
      'Work Orders, Assets, Locations',
      'Basic dashboard (open WOs, asset list)',
      'Mobile friendly access',
      'Email support',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '£149',
    cadence: '/ month',
    description: 'Everything growing maintenance teams need to stay proactive.',
    features: [
      'Up to 25 users',
      'Work Orders, Assets, Locations, Preventive Maintenance, Parts & Inventory',
      'Dashboards (WO backlog, overdue PMs, top asset issues)',
      'Role-based permissions (Admin, Engineer, Shop Floor)',
      'API & integrations (Microsoft 365, email, exports)',
      'Email + chat support',
    ],
    highlight: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'from £499',
    cadence: '/ month',
    description: 'Built for multi-site operations that require tailored workflows.',
    features: [
      'Unlimited users',
      'All modules: WO, Assets, Locations, Preventive Maintenance, Parts & Inventory, Health & Safety, Advanced Dashboards',
      'Integrations: SAP, QuickBooks, ERP',
      'IoT sensor support (optional add-on)',
      'White-label option',
      'Dedicated account manager + 24/7 support SLA',
    ],
  },
];

@customElement('pristinemaint-pricing')
export class PristineMaintPricing extends LitElement {
  static styles = [landingStyles, pricingPageStyles];

  protected firstUpdated() {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }

  private emitNavigate(route: string, section?: string) {
    this.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { route, section },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private navHandler(route: string, section?: string) {
    return (event: Event) => {
      event.preventDefault();
      this.emitNavigate(route, section);
    };
  }

  private handleLogoKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.emitNavigate('/');
    }
  }

  render() {
    return html`
      <div class="landing-container">
        <header class="header">
          <div
            class="logo"
            @click=${this.navHandler('/')}
            @keydown=${this.handleLogoKeydown}
            role="link"
            tabindex="0"
          >
            <img
              src="https://firebasestorage.googleapis.com/v0/b/work-order-managment.firebasestorage.app/o/pristinemaint.png?alt=media&token=2dd117e4-d4e7-4bd6-b666-548c5068b566"
              alt="PristineMaint Logo"
              class="logo-image"
            />
            <span class="logo-text">PristineMaint</span>
          </div>

          <nav class="nav-links">
            <a href="/features" class="nav-link" @click=${this.navHandler('/features')}>Features</a>
            <a href="/pricing" class="nav-link active" aria-current="page" @click=${this.navHandler('/pricing')}>Pricing</a>
            <a href="#about" class="nav-link">About</a>
            <a href="#contact" class="nav-link" @click=${this.navHandler('/', 'contact')}>Contact</a>
          </nav>

          <div class="header-actions">
            <button class="btn btn-secondary" @click=${this.navHandler('/', 'features')}>Learn More</button>
            <button class="btn btn-primary" @click=${this.navHandler('/', 'contact')}>Get Started</button>
          </div>
        </header>

        <main class="pricing-page">
          <section class="pricing-hero">
            <h1>Simple Pricing, Powerful Features</h1>
            <p>
              Choose the plan that matches your maintenance maturity. Upgrade anytime as your team grows into
              advanced workflows and integrations.
            </p>
          </section>

          <section class="pricing-grid">
            <div class="pricing-cards">
              ${PRICING_TIERS.map(
                (tier) => html`
                  <article class="pricing-card ${tier.highlight ? 'highlight' : ''}" id=${tier.id}>
                    <h2>${tier.name}</h2>
                    <div class="pricing-price">
                      ${tier.price} <span>${tier.cadence}</span>
                    </div>
                    <p class="pricing-description">${tier.description}</p>
                    <ul class="pricing-features">
                      ${tier.features.map((feature) => html`<li>${feature}</li>`)}
                    </ul>
                    <sl-button variant=${tier.highlight ? 'primary' : 'default'} size="large" @click=${this.navHandler('/', 'contact')}>
                      Get Started
                    </sl-button>
                  </article>
                `,
              )}
            </div>

            <div class="pricing-actions">
              <sl-button variant="primary" size="large" @click=${this.navHandler('/', 'contact')}>
                Book a tailored demo
              </sl-button>
              <p class="pricing-footer-note">
                Need a custom deployment? Contact us for data migration, training, and white-glove onboarding.
              </p>
            </div>
          </section>
        </main>

        <footer class="footer">
          <div class="footer-container">
            <div class="footer-section">
              <h3>PristineMaint</h3>
              <p>Advanced CMMS solutions for modern businesses. Streamline your maintenance operations with our cutting-edge platform.</p>
            </div>

            <div class="footer-section">
              <h3>Product</h3>
              <ul class="footer-links">
                <li><a href="/features" @click=${this.navHandler('/features')}>Features</a></li>
                <li><a href="/pricing" @click=${this.navHandler('/pricing')}>Pricing</a></li>
                <li><a href="#demo">Demo</a></li>
                <li><a href="#integrations">Integrations</a></li>
              </ul>
            </div>

            <div class="footer-section">
              <h3>Company</h3>
              <ul class="footer-links">
                <li><a href="#about">About Us</a></li>
                <li><a href="#careers">Careers</a></li>
                <li><a href="#blog">Blog</a></li>
                <li><a href="#press">Press</a></li>
              </ul>
            </div>

            <div class="footer-section">
              <h3>Support</h3>
              <ul class="footer-links">
                <li><a href="#help">Help Center</a></li>
                <li>
                  <a href="mailto:info@pristinemaint.com?subject=Support Request&body=Hello, I have a question about PristineMaint.">
                    Contact Us
                  </a>
                </li>
                <li><a href="#status">Status</a></li>
                <li><a href="#security">Security</a></li>
              </ul>
            </div>
          </div>

          <div class="footer-bottom">
            <p>&copy; 2024 PristineMaint. All rights reserved.</p>
          </div>
        </footer>
      </div>
    `;
  }
}
